import {
    Alert,
    Box,
    Button,
    LinearProgress,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { replanningReviewedDosesState } from '~/state/atoms/adaptiveReplanningAtoms';
import DoseScaleField from './DoseScaleField';
import { REPLANNING_ALERT_SX } from './replanningAlertSx';
import { ReviewTransformKind } from './reviewTransforms';
import { useReviewDatasets } from './useReviewDatasets';
import { voxelPercent } from './voxelPercent';
import { ReviewTargetDose } from './useReviewTargetDose';
import { doseLabel } from '~/domain/projections/factories';

const ALERT_SX = { ...REPLANNING_ALERT_SX, mx: 0 };

function Row({
    labelId,
    value,
    testId,
}: {
    labelId: string;
    value?: string;
    testId: string;
}) {
    return (
        <Box display="flex" gap={1} fontSize={11.5} data-testid={testId}>
            <Box color="text.secondary" minWidth={52} flexShrink={0}>
                <FormattedMessage id={labelId} />
            </Box>
            <Box flex={1} minWidth={0} sx={{ wordBreak: 'break-word' }}>
                {value ?? <FormattedMessage id="replanning.dose.none" />}
            </Box>
        </Box>
    );
}

interface ReviewDoseSectionProps {
    readonly target: ReviewTargetDose;
    readonly kind?: ReviewTransformKind;
    readonly disabledReasonId?: string;
}

export default function ReviewDoseSection({
    target,
    kind,
    disabledReasonId,
}: ReviewDoseSectionProps) {
    const intl = useIntl();
    const { priorDose } = useReviewDatasets();
    const [reviewedDoses, setReviewedDoses] = useRecoilState(
        replanningReviewedDosesState
    );
    const { dose, record, status, blockedReasonId, retry } = target;

    const isReviewed = !!dose && reviewedDoses.includes(dose.sopInstanceUid);
    const toggleReviewed = (reviewed: boolean) => {
        if (!dose) {
            return;
        }
        setReviewedDoses((current) =>
            reviewed
                ? [...current, dose.sopInstanceUid]
                : current.filter((uid) => uid !== dose.sopInstanceUid)
        );
    };
    const unmappedPercent = record
        ? voxelPercent(record.unmappedVoxels, record.totalVoxels)
        : 0;
    const outsidePercent = record
        ? voxelPercent(record.outsideSourceVoxels, record.totalVoxels)
        : 0;

    return (
        <Stack spacing={0.75}>
            <Row
                labelId="replanning.dose.source"
                value={priorDose && doseLabel(priorDose)}
                testId="replanning-dose-source"
            />
            <Row
                labelId="replanning.dose.target"
                value={dose && doseLabel(dose)}
                testId="replanning-dose-target"
            />
            {kind && dose && (
                <Typography
                    fontSize={11}
                    color="text.secondary"
                    data-testid="replanning-dose-provenance"
                >
                    <FormattedMessage
                        id={`replanning.dose.provenance.${kind}`}
                    />
                </Typography>
            )}
            {blockedReasonId && (
                <Alert
                    severity="info"
                    variant="outlined"
                    sx={ALERT_SX}
                    data-testid="replanning-dose-blocked"
                >
                    <FormattedMessage id={blockedReasonId} />
                </Alert>
            )}
            {status?.status === 'mapping' && (
                <Box data-testid="replanning-dose-mapping">
                    <Typography fontSize={11} color="text.secondary">
                        <FormattedMessage id="replanning.dose.mapping" />
                    </Typography>
                    <LinearProgress sx={{ mt: 0.5 }} />
                </Box>
            )}
            {status?.status === 'failed' && (
                <Alert
                    severity="error"
                    variant="outlined"
                    sx={ALERT_SX}
                    data-testid="replanning-dose-mapping-failed"
                    action={
                        <Button
                            size="small"
                            onClick={retry}
                            sx={{ fontSize: 11 }}
                        >
                            <FormattedMessage id="replanning.dose.retry" />
                        </Button>
                    }
                >
                    <FormattedMessage id={status.messageId} />
                </Alert>
            )}
            {unmappedPercent > 0 && (
                <Alert
                    severity="warning"
                    variant="outlined"
                    sx={ALERT_SX}
                    data-testid="replanning-dose-unmapped"
                >
                    <FormattedMessage
                        id="replanning.dose.unmapped"
                        values={{
                            percent: intl.formatNumber(unmappedPercent, {
                                maximumFractionDigits: 1,
                            }),
                        }}
                    />
                </Alert>
            )}
            {outsidePercent > 0 && (
                <Alert
                    severity="info"
                    variant="outlined"
                    sx={ALERT_SX}
                    data-testid="replanning-dose-outside-source"
                >
                    <FormattedMessage
                        id="replanning.dose.outsideSource"
                        values={{
                            percent: intl.formatNumber(outsidePercent, {
                                maximumFractionDigits: 1,
                            }),
                        }}
                    />
                </Alert>
            )}
            <DoseScaleField disabled={!!blockedReasonId} />
            {dose && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    data-testid="replanning-dose-approval"
                >
                    <Box
                        fontSize={11.5}
                        color={isReviewed ? 'success.main' : 'text.secondary'}
                    >
                        <FormattedMessage
                            id={
                                isReviewed
                                    ? 'replanning.dose.approved'
                                    : 'replanning.dose.unapproved'
                            }
                        />
                    </Box>
                    {isReviewed ? (
                        <Button
                            size="small"
                            variant="text"
                            sx={{ fontSize: 11, whiteSpace: 'nowrap' }}
                            onClick={() => toggleReviewed(false)}
                            data-testid="replanning-dose-unapprove"
                        >
                            <FormattedMessage id="replanning.dose.unapprove" />
                        </Button>
                    ) : (
                        <Tooltip
                            title={
                                disabledReasonId
                                    ? intl.formatMessage({
                                          id: disabledReasonId,
                                      })
                                    : ''
                            }
                        >
                            <span>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="warning"
                                    disableElevation
                                    disabled={!!disabledReasonId}
                                    sx={{
                                        fontSize: 11,
                                        whiteSpace: 'nowrap',
                                    }}
                                    onClick={() => toggleReviewed(true)}
                                    data-testid="replanning-dose-approve"
                                >
                                    <FormattedMessage id="replanning.dose.approve" />
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Stack>
            )}
        </Stack>
    );
}
